import { useState } from "react";
import type { DecisionNode } from "@/mocks/protocolResponses";

interface Props {
  nodes: DecisionNode[];
}

export default function ProtocolDecisionFlow({ nodes }: Props) {
  if (nodes.length === 0) return null;

  return (
    <div>
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-7 h-7 rounded-lg bg-secondary-100 flex items-center justify-center">
          <i className="ri-git-branch-line text-secondary-600 text-sm" />
        </div>
        <h3 className="text-base font-semibold text-foreground-900">Flujograma de Decisión</h3>
      </div>

      <div className="space-y-4">
        {nodes.map((node, nodeIdx) => (
          <DecisionNodeCard key={nodeIdx} node={node} nodeIdx={nodeIdx} />
        ))}
      </div>
    </div>
  );
}

function DecisionNodeCard({ node, nodeIdx }: { node: DecisionNode; nodeIdx: number }) {
  const [selectedPath, setSelectedPath] = useState<number | null>(null);

  return (
    <div className="relative">
      {/* Question node */}
      <div className="bg-white border-2 border-secondary-200 rounded-xl overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 bg-secondary-50/50 border-b border-secondary-100">
          <div className="w-8 h-8 rounded-lg bg-secondary-500 text-white flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold">{nodeIdx + 1}</span>
          </div>
          <p className="text-sm font-semibold text-foreground-900">{node.pregunta}</p>
        </div>

        <div className="divide-y divide-background-100">
          {node.opciones.map((opt, optIdx) => {
            const isSelected = selectedPath === optIdx;
            return (
              <button
                key={optIdx}
                onClick={() => setSelectedPath(isSelected ? null : optIdx)}
                className={`w-full text-left px-4 py-3.5 transition-all cursor-pointer group ${
                  isSelected ? "bg-secondary-50" : "hover:bg-background-50"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                      isSelected
                        ? "bg-secondary-500 border-secondary-500 text-white"
                        : "border-background-300 text-foreground-400 group-hover:border-secondary-300"
                    }`}
                  >
                    <span className="text-xs font-bold">
                      {opt.respuesta.charAt(0)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                        opt.respuesta === "Sí"
                          ? "bg-accent-100 text-accent-800"
                          : opt.respuesta === "No"
                          ? "bg-red-100 text-red-800"
                          : "bg-background-200 text-foreground-600"
                      }`}>
                        {opt.respuesta}
                      </span>
                    </div>
                    <p className={`text-sm leading-relaxed transition-all ${
                      isSelected ? "text-foreground-900" : "text-foreground-600"
                    }`}>
                      {opt.accion}
                    </p>
                    {isSelected && opt.fuente && (
                      <p className="text-xs text-primary-600 mt-1.5 flex items-center gap-1">
                        <i className="ri-link text-xs" />
                        {opt.fuente}
                      </p>
                    )}
                  </div>
                  <i
                    className={`text-sm flex-shrink-0 mt-1 transition-all ${
                      isSelected
                        ? "ri-arrow-right-circle-fill text-secondary-500"
                        : "ri-arrow-right-circle-line text-foreground-300 group-hover:text-foreground-500"
                    }`}
                  />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Connector line */}
      {nodeIdx < 3 && (
        <div className="flex justify-center py-1">
          <div className="w-0.5 h-4 bg-secondary-200" />
        </div>
      )}
    </div>
  );
}